// UI / view state store.
//
// Holds everything that is a view concern rather than server data: current section, search,
// quick + select filters, sort, column widths/visibility, density, timezone mode, alert
// thresholds, saved views, local account-label aliases, drawer/detail selection, and the
// transient overlay/toast state. Durable preferences persist to localStorage; the current
// section + filters + sort mirror to the URL hash for shareable links (like the mockup).

import { DEFAULT_THRESHOLDS, type Thresholds } from '../lib/mappers';
import type { TzMode } from '../lib/format';
import { Store } from './store';

export type NavKey =
  | 'overview'
  | 'tradingVps'
  | 'accounts'
  | 'positions'
  | 'alerts'
  | 'vps'
  | 'detail'
  | 'settings'
  | 'docs';

export type Density = 'compact' | 'comfortable';

export interface SelectFilters {
  vps: string;
  broker: string;
  ccy: string;
  risk: string;
  conn: string;
  strat: string;
}

export interface SortState {
  k: string;
  d: number;
}

export interface SavedView {
  name: string;
  snap: { q: string; quick: Record<string, boolean>; f: SelectFilters; sort: SortState };
}

export interface UiState {
  nav: NavKey;
  q: string;
  quick: Record<string, boolean>;
  f: SelectFilters;
  sort: SortState;

  colW: Record<string, number>;
  hidden: Record<string, boolean>;
  dens: Density;
  tz: TzMode;
  thr: Thresholds;

  views: SavedView[];
  viewSel: string;
  saveOpen: boolean;
  viewName: string;

  labels: Record<string, string>;

  drawerId: string | null;
  detailId: string | null;

  posGroup: string;
  posDir: '' | 'buy' | 'sell';

  aSev: '' | 'critical' | 'warning' | 'info';
  /** UI filter: `active` folds the pending+firing lifecycle states together. */
  aState: '' | 'active' | 'acknowledged' | 'resolved';

  colMenu: boolean;
  sheetOpen: boolean;
  simMenu: boolean;
  narrow: boolean;

  editLabel: boolean;
  labelVal: string;

  toast: string;
  announce: string;
}

export const EMPTY_FILTERS: SelectFilters = { vps: '', broker: '', ccy: '', risk: '', conn: '', strat: '' };

const PERSIST_KEY = 'wt_ui_v1';

interface Persisted {
  dens: Density;
  tz: TzMode;
  thr: Thresholds;
  hidden: Record<string, boolean>;
  colW: Record<string, number>;
  views: SavedView[];
  labels: Record<string, string>;
}

function loadPersisted(): Partial<UiState> {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return {};
    const p = JSON.parse(raw) as Partial<Persisted>;
    return {
      dens: p.dens === 'comfortable' ? 'comfortable' : 'compact',
      tz: p.tz === 'local' || p.tz === 'broker' ? p.tz : 'both',
      thr: { ...DEFAULT_THRESHOLDS, ...(p.thr ?? {}) },
      hidden: p.hidden ?? {},
      colW: p.colW ?? {},
      views: Array.isArray(p.views) ? p.views : [],
      labels: p.labels ?? {},
    };
  } catch {
    return {};
  }
}

const initialUiState: UiState = {
  nav: 'overview',
  q: '',
  quick: {},
  f: { ...EMPTY_FILTERS },
  sort: { k: 'sev', d: 1 },
  colW: {},
  hidden: {},
  dens: 'compact',
  tz: 'both',
  thr: { ...DEFAULT_THRESHOLDS },
  views: [],
  viewSel: '',
  saveOpen: false,
  viewName: '',
  labels: {},
  drawerId: null,
  detailId: null,
  posGroup: 'account',
  posDir: '',
  aSev: '',
  aState: '',
  colMenu: false,
  sheetOpen: false,
  simMenu: false,
  narrow: false,
  editLabel: false,
  labelVal: '',
  toast: '',
  announce: '',
  ...loadPersisted(),
};

export const uiStore = new Store<UiState>(initialUiState);

// Persist durable prefs on change.
let saveTimer: ReturnType<typeof setTimeout> | null = null;
uiStore.subscribe(() => {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    const s = uiStore.get();
    const persisted: Persisted = {
      dens: s.dens,
      tz: s.tz,
      thr: s.thr,
      hidden: s.hidden,
      colW: s.colW,
      views: s.views,
      labels: s.labels,
    };
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(persisted));
    } catch {
      /* storage full / disabled — non-fatal */
    }
  }, 250);
});

// ---- Actions --------------------------------------------------------------------------

export function navGo(nav: NavKey): void {
  uiStore.set({ nav, simMenu: false, colMenu: false, sheetOpen: false });
}

export function openDrawer(id: string): void {
  uiStore.set({ drawerId: id, editLabel: false });
}

export function closeDrawer(): void {
  uiStore.set({ drawerId: null, editLabel: false });
}

export function openDetail(id: string): void {
  uiStore.set({ nav: 'detail', detailId: id, drawerId: null });
}

export function clearFilters(): void {
  uiStore.set({ q: '', quick: {}, f: { ...EMPTY_FILTERS }, viewSel: '' });
}

export function toggleQuick(key: string): void {
  uiStore.set((s) => {
    const quick = { ...s.quick };
    if (quick[key]) delete quick[key];
    else quick[key] = true;
    return { quick, viewSel: '' };
  });
}

export function setFilter(id: keyof SelectFilters, value: string): void {
  uiStore.set((s) => ({ f: { ...s.f, [id]: value }, viewSel: '' }));
}

export function setSort(k: string, def: number): void {
  uiStore.set((s) => {
    if (s.sort.k !== k) return { sort: { k, d: def } };
    if (s.sort.d === def) return { sort: { k, d: -def } };
    return { sort: { k: 'sev', d: 1 } };
  });
}

export function setColWidth(id: string, w: number): void {
  uiStore.set((s) => ({ colW: { ...s.colW, [id]: Math.max(56, w) } }));
}

export function toggleColumn(id: string): void {
  uiStore.set((s) => {
    const hidden = { ...s.hidden };
    if (hidden[id]) delete hidden[id];
    else hidden[id] = true;
    return { hidden };
  });
}

export function saveView(): void {
  uiStore.set((s) => {
    const name = s.viewName.trim() || 'View ' + (s.views.length + 1);
    const views = s.views.concat([{ name, snap: { q: s.q, quick: s.quick, f: s.f, sort: s.sort } }]);
    return { views, viewSel: 's:' + (views.length - 1), saveOpen: false, viewName: '' };
  });
}

export function setThreshold(key: keyof Thresholds, value: number): void {
  if (!Number.isFinite(value) || value <= 0) return;
  uiStore.set((s) => ({ thr: { ...s.thr, [key]: value } }));
}

/** Set a local display alias for an account (never touches the broker account). */
export function setLabelAlias(id: string, label: string): void {
  uiStore.set((s) => ({ labels: { ...s.labels, [id]: label }, editLabel: false }));
}

export function displayLabel(id: string, serverLabel: string): string {
  return uiStore.get().labels[id] ?? serverLabel;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toastMsg(t: string): void {
  uiStore.set({ toast: t });
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => uiStore.set({ toast: '' }), 4500);
}

export function announce(text: string): void {
  uiStore.set({ announce: text });
}
